'use client';

import { IAllOrdersEnum, IAllStatusInclAll, IOrderFilters, IOrderShipped } from 'my-poppies-b2b-types';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { fetchOrdersFromApi } from '@/api/fetchOrdersFromApi';
import { useApi } from '@/api/useApi';
import { intervalMs } from '@/config';
import { DataContext } from '@/contexts/DataContext';
import { SettingsContext } from '@/contexts/SettingsContext';
import useAllSearchParams from '@/hooks/useAllSearchParams';
import { useAppPath } from '@/hooks/useAppPath';
import { IAutoRefreshSettings } from '@/types/IAutoRefreshSettings';
import { IFetchState } from '@/types/IFetchState';
import { ILastIntervalFetchInfo } from '@/types/ILastIntervalFetchInfo';
import { IOrderApproveData } from '@/types/IOrderApproveData';
import { IOrderCollectionAll } from '@/types/IOrderCollection';
import { IOrderContextProps } from '@/types/IOrderContextProps';
import { IOrderRedirectInfo } from '@/types/IOrderRedirectInfo';
import { IOrderSorting } from '@/types/IOrderSorting';
import { cloneAnything } from '@/utils/cloneAnything';
import { getNowAsNumberInMilliseconds } from '@/utils/getNow';

export const OrderContext = createContext<IOrderContextProps>({} as IOrderContextProps);

const emptySorting: IOrderSorting = undefined;
const emptyFilters: IOrderFilters = { q: '' };

export const OrderContextProvider = ({ children }: PropsWithChildren) => {
  const [lastIntervalFetchInfo, setLastIntervalFetchInfo] = useState<ILastIntervalFetchInfo>({
    finishedAt: getNowAsNumberInMilliseconds(),
    result: 'UNKNOWN',
  });
  const { callApi } = useApi();
  const [page, setPage] = useState<number>(0); // TODO default value  #12345
  const [pageSize, setPageSize] = useState<number>(5); // TODO default value  #12345
  const pageSizePrev = useRef<number>(5); // TODO default value  #12345
  const keepOthers = useRef<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availablePageSizes, setAvailablePageSizes] = useState<number[]>([5, 10, 25, 100]); // TODO default value #12345
  const [orderCollection, setOrderCollection] = useState<IOrderCollectionAll>({});
  const [fetchState, setFetchState] = useState<IFetchState>('FETCHING');
  const [approveNewOrderData, setApproveNewOrderData] = useState<IOrderApproveData | undefined>(undefined);
  const [showRedirectForOrder, setShowRedirectForOrder] = useState<IOrderRedirectInfo | undefined>(undefined);
  const [showShippingStatusModalForOrder, setShowShippingStatusModalForOrder] = useState<IOrderShipped | undefined>(
    undefined
  );
  const [orderSearchInputValue, setOrderSearchInputValue] = useState('');
  const [orderFilters, setOrderFilters] = useState<IOrderFilters>(emptyFilters);
  const [orderSorting, setOrderSorting] = useState<IOrderSorting>(emptySorting);
  const someFilterIsSet = useMemo(() => {
    return orderSearchInputValue.trim().length > 0 || Object.values(orderFilters).some((f) => Boolean(f));
  }, [orderFilters, orderSearchInputValue]);
  const someSortingIsSet = useMemo(() => Object.values(orderSorting ?? {}).some((f) => Boolean(f)), [orderSorting]);
  const [showOrderFilters, setShowOrderFilters] = useState<boolean>(false);
  const orderStatusPrev = useRef<IAllStatusInclAll>(null);
  const filtersPrev = useRef<IOrderFilters>(null);
  const sortingPrev = useRef<IOrderSorting>(null);
  const { pathname } = useAppPath();
  const { visibleSectionsNamesForUser } = useContext(SettingsContext);
  const { updateCount } = useContext(DataContext);
  const allSearchParams = useAllSearchParams();
  const statusFromPage = allSearchParams?.status as IAllStatusInclAll | undefined;
  const lastFetchAt = useRef<number>(0);

  const newOrdersSectionIsVisible = useMemo(
    () => pathname === '/' && visibleSectionsNamesForUser.includes('NEW_ORDERS'),
    [pathname, visibleSectionsNamesForUser]
  );

  const declinedOrdersSectionIsVisible = useMemo(
    () => pathname === '/' && visibleSectionsNamesForUser.includes('DECLINED_ORDERS'),
    [pathname, visibleSectionsNamesForUser]
  );

  const anyOverviewSectionIsVisible = useMemo(
    () =>
      pathname === '/' &&
      (visibleSectionsNamesForUser.includes('ORDER_OVERVIEW') ||
        visibleSectionsNamesForUser.includes('PREORDER_OVERVIEW')),
    [pathname, visibleSectionsNamesForUser]
  );

  const autoRefreshSettings: IAutoRefreshSettings = useMemo(
    () => ({
      refreshNewOrders: newOrdersSectionIsVisible,
      refreshDeclinedOrders: declinedOrdersSectionIsVisible,
      refreshCount: newOrdersSectionIsVisible || declinedOrdersSectionIsVisible || anyOverviewSectionIsVisible,
    }),
    [anyOverviewSectionIsVisible, declinedOrdersSectionIsVisible, newOrdersSectionIsVisible]
  );

  const resetOrderFilters = useCallback(() => {
    setOrderFilters(emptyFilters);
  }, []);

  const resetOrderSorting = useCallback(() => {
    setOrderSorting(emptySorting);
  }, []);

  const alreadyFetchedOrdersAsArray: IAllOrdersEnum[] = useMemo(() => {
    const orders: IAllOrdersEnum[] = [];
    Object.values(orderCollection).forEach((ordersPerStatus) => {
      orders.push(...ordersPerStatus.itemsOnPage);
    });
    return orders;
  }, [orderCollection]);

  const getOrderById = useCallback(
    (orderId: string | undefined) => {
      if (!orderId) {
        return undefined;
      }
      return alreadyFetchedOrdersAsArray.find((order) => order.id === orderId);
    },
    [alreadyFetchedOrdersAsArray]
  );

  const fetchOrders = useCallback(
    async (
      orderStatus: IAllStatusInclAll | IAllStatusInclAll[],
      page = 0,
      pageSize = 50,
      filters: IOrderFilters = {},
      sorting?: IOrderSorting,
      includeUpdateCount = true
    ) => {
      if (JSON.stringify(orderStatus) !== JSON.stringify(orderStatusPrev.current) && page !== 0) {
        setPage(0);
        return;
      }

      if (!Array.isArray(orderStatus)) {
        orderStatusPrev.current = orderStatus;
      }

      if (JSON.stringify(filters) !== JSON.stringify(filtersPrev.current) && page !== 0) {
        setPage(0);
        return;
      }
      filtersPrev.current = filters;

      if (JSON.stringify(sorting) !== JSON.stringify(sortingPrev.current) && page !== 0) {
        setPage(0);
        return;
      }
      sortingPrev.current = sorting;

      if (pageSize > pageSizePrev.current || keepOthers.current) {
        setFetchState('FETCHING_ADDITIONAL');
      } else {
        setFetchState('FETCHING');
      }
      pageSizePrev.current = pageSize;

      const statusArray = Array.isArray(orderStatus) ? orderStatus : [orderStatus];

      const allOrderFetchPromises = statusArray.map((os) =>
        fetchOrdersFromApi(callApi, os, page, pageSize, filters, sorting)
      );

      const fetchStartAt = getNowAsNumberInMilliseconds();
      lastFetchAt.current = fetchStartAt;
      const allOrderFetchResults = await Promise.all(allOrderFetchPromises);

      if (lastFetchAt.current !== fetchStartAt) {
        // don't set the response to the state, if it is outdated
        return;
      }

      setOrderCollection((prevState) => {
        const newState: IOrderCollectionAll = cloneAnything(prevState);

        statusArray.forEach((os, index) => {
          const paginationResponse = allOrderFetchResults[index];
          const fetchedOrders = paginationResponse.itemsOnPage;
          const itemsOnPage = keepOthers.current
            ? [...(newState?.[os]?.itemsOnPage ?? []), ...fetchedOrders]
            : [...fetchedOrders];
          keepOthers.current = false;

          // @ts-expect-error fix this at some point
          newState[os] = { ...paginationResponse, itemsOnPage };
        });

        return newState;
      });

      if (includeUpdateCount) {
        await updateCount();
      }

      // TODO avoid race conditions if user clicks multiple times quickly #12345
      setFetchState('READY');
    },
    [callApi, updateCount]
  );

  useEffect(() => {
    const updateOrders = async () => {
      if (statusFromPage) {
        await fetchOrders(statusFromPage, page, pageSize, orderFilters, orderSorting);
      } else {
        const orderStatusArray: IAllStatusInclAll[] = [];
        if (newOrdersSectionIsVisible) {
          orderStatusArray.push('NEW');
        }
        if (declinedOrdersSectionIsVisible) {
          orderStatusArray.push('DECLINED');
        }
        await fetchOrders(orderStatusArray, 0, pageSize);
        if (pathname === '/') {
          setLastIntervalFetchInfo({ finishedAt: getNowAsNumberInMilliseconds(), result: 'SUCCESS' });
        }
      }
    };

    updateOrders();
  }, [
    fetchOrders,
    orderFilters,
    orderSorting,
    page,
    pageSize,
    statusFromPage,
    newOrdersSectionIsVisible,
    declinedOrdersSectionIsVisible,
    pathname,
  ]);

  useEffect(() => {
    const run = async () => {
      try {
        const statuses: IAllStatusInclAll[] = [];
        if (autoRefreshSettings.refreshNewOrders) {
          statuses.push('NEW');
        }
        if (autoRefreshSettings.refreshDeclinedOrders) {
          statuses.push('DECLINED');
        }

        const promises: Promise<unknown>[] = [];
        if (statuses.length > 0) {
          promises.push(fetchOrders(statuses, 0, pageSize, undefined, undefined, false));
        }

        if (autoRefreshSettings.refreshCount) {
          promises.push(updateCount());
        }

        await Promise.all(promises);

        setLastIntervalFetchInfo({ finishedAt: getNowAsNumberInMilliseconds(), result: 'SUCCESS' });

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        setLastIntervalFetchInfo({ finishedAt: getNowAsNumberInMilliseconds(), result: 'ERROR' });
      }
    };

    const needToRefresh = Object.values(autoRefreshSettings).some((a) => a);
    const id = needToRefresh ? setInterval(run, intervalMs) : undefined;

    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [autoRefreshSettings, fetchOrders, pageSize, pathname, updateCount]);

  const startOrderApprove = (orderId: string, isPositive: boolean) => {
    setApproveNewOrderData({ orderId, isPositive });
  };

  const resetOrderApprove = () => {
    setApproveNewOrderData(undefined);
  };

  const setOrderReason = (reason: string) => {
    setApproveNewOrderData((prevState) => {
      if (!prevState) {
        return undefined;
      }
      return { ...cloneAnything(prevState), reason };
    });
  };

  useEffect(() => {
    setPage(0);
  }, [orderFilters]);

  const _setPage = useCallback((desiredPage: number, _keepOthers = false) => {
    keepOthers.current = _keepOthers;
    setPage(desiredPage);
  }, []);

  const value: IOrderContextProps = useMemo((): IOrderContextProps => {
    return {
      orderCollection,
      alreadyFetchedOrdersAsArray,
      newOrdersTruncated: (orderCollection?.NEW?.itemsOnPage ?? []).slice(0, 3),
      declinedOrdersTruncated: (orderCollection?.DECLINED?.itemsOnPage ?? []).slice(0, 3),
      approveNewOrderData,
      startOrderApprove,
      resetOrderApprove,
      setOrderReason,
      pageSize,
      setPageSize,
      availablePageSizes,
      page,
      setPage: _setPage,
      fetchState,
      setFetchState,
      orderFilters,
      orderSorting,
      setOrderSorting,
      setOrderFilters,
      showOrderFilters,
      setShowOrderFilters,
      someFilterIsSet,
      someSortingIsSet,
      getOrderById,
      resetOrderFilters,
      resetOrderSorting,
      showRedirectForOrder,
      setShowRedirectForOrder,
      showShippingStatusModalForOrder,
      setShowShippingStatusModalForOrder,
      orderSearchInputValue,
      setOrderSearchInputValue,
      fetchOrders,
      lastIntervalFetchInfo,
    };
  }, [
    orderCollection,
    alreadyFetchedOrdersAsArray,
    approveNewOrderData,
    pageSize,
    availablePageSizes,
    page,
    _setPage,
    fetchState,
    orderFilters,
    orderSorting,
    showOrderFilters,
    someFilterIsSet,
    someSortingIsSet,
    getOrderById,
    resetOrderFilters,
    resetOrderSorting,
    showRedirectForOrder,
    showShippingStatusModalForOrder,
    setShowShippingStatusModalForOrder,
    orderSearchInputValue,
    setOrderSearchInputValue,
    fetchOrders,
    lastIntervalFetchInfo,
  ]);

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};
